// frontend/user/src/App.js

import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";

// --- 1. IMPORTACIONES AÑADIDAS Y MODIFICADAS ---
// Providers de Contexto
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TourProvider } from "./contexts/TourContext";

// Componentes de Layout Global (se mantienen como imports directos - se usan siempre)
import Header from "./components/Header";
import Footer from "./components/Footer";
import FloatingTourButton from "./components/FloatingTourButton";
import LoadingSpinner from "./components/LoadingSpinner";
import PWAInstallBanner from "./components/PWAInstallBanner";

// Componentes Auxiliares (se mantienen como imports directos - se usan siempre)
import PrivateRoute from "./components/PrivateRoute";
import { Toaster } from "react-hot-toast";
import "./App.css";

// ✅ OPTIMIZACIÓN 2.1: Preloading inteligente
import { usePreload } from "./utils/preload";

// ✅ OPTIMIZACIÓN 1.1: Lazy Loading de Páginas
// Todas las páginas se cargan bajo demanda para reducir bundle inicial
const HomePage = lazy(() => import("./pages/HomePage"));
const ItemsPage = lazy(() => import("./pages/ItemsPage"));
const ItemDetailPage = lazy(() => import("./pages/ItemDetailPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const EmailVerificationPage = lazy(() => import("./pages/EmailVerificationPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PasswordResetSentPage = lazy(() => import("./pages/PasswordResetSentPage"));
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const UserBookings = lazy(() => import("./pages/UserBookings"));
const BookingDetails = lazy(() => import("./components/BookingDetails"));
const CreateBooking = lazy(() => import("./components/CreateBooking"));
const CreateItemPage = lazy(() => import("./pages/CreateItemPage"));
const RentalConfirmationPage = lazy(() => import("./pages/RentalConfirmationPage"));
const PaymentStatusPage = lazy(() => import("./pages/PaymentStatusPage"));
const PaymentHistoryPage = lazy(() => import("./pages/PaymentHistoryPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AyudaPage = lazy(() => import("./pages/AyudaPage"));
const TerminosPage = lazy(() => import("./pages/TerminosPage"));
const PrivacidadPage = lazy(() => import("./pages/PrivacidadPage"));
const PoliticaCookiesPage = lazy(() => import("./pages/PoliticaCookiesPage"));
const EditItemPage = lazy(() => import("./pages/EditItemPage"));
const DisputeForm = lazy(() => import("./components/DisputeForm"));
const UserDisputesPage = lazy(() => import("./pages/UserDisputesPage"));
const DisputeDetailPage = lazy(() => import("./pages/DisputeDetailPage"));
const PaymentSplitPage = lazy(() => import("./pages/PaymentSplitPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentFailurePage = lazy(() => import("./pages/PaymentFailurePage"));
const PaymentPendingPage = lazy(() => import("./pages/PaymentPendingPage"));
const UnifiedPaymentPage = lazy(() => import("./pages/UnifiedPaymentPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const CVUConfiguration = lazy(() => import("./components/Account/CVUConfiguration"));
const SimplePaymentTest = lazy(() => import("./components/Payment/SimplePaymentTest"));
const CreateRentalRequestPage = lazy(() => import("./pages/CreateRentalRequestPage"));
const RentalRequestsPage = lazy(() => import("./pages/RentalRequestsPage"));
const RentalRequestDetailPage = lazy(() => import("./pages/RentalRequestDetailPage"));

// --- 2. AppContent MODIFICADO PARA INCLUIR EL LAYOUT ---
// Ahora AppContent se encarga de renderizar la estructura principal de la página.
function AppContent() {
  const { user } = useAuth(); // Usar 'user' en lugar de 'currentUser' para consistencia
  const location = useLocation();
  
  // ✅ OPTIMIZACIÓN 2.1: Preload de rutas probables
  usePreload(location.pathname);

  return (
    // El div principal se asegura que el footer quede abajo.
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Toaster position="top-right" />
      <Header /> {/* El Header se renderiza una sola vez, aquí. */}
      <PWAInstallBanner /> {/* Banner para instalar PWA */}
      <main className="flex-grow">
        {/* ✅ OPTIMIZACIÓN 1.1: Suspense para lazy loading */}
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
          {/* Todas tus rutas permanecen igual */}
          <Route path="/" element={<HomePage />} />
          <Route path="/item/:id" element={<ItemDetailPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/email-verification"
            element={<EmailVerificationPage />}
          />
          <Route
            path="/forgot-password"
            element={
              !user ? <ForgotPasswordPage /> : <Navigate to="/" />
            }
          />
          <Route
            path="/password-reset-sent"
            element={
              !user ? <PasswordResetSentPage /> : <Navigate to="/" />
            }
          />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/items" element={<ItemsPage />} />
          <Route path="/rental-requests" element={<RentalRequestsPage />} />
          <Route path="/rental-requests/create" element={<CreateRentalRequestPage />} />
          <Route path="/rental-requests/:id" element={<RentalRequestDetailPage />} />
          <Route
            path="/rentals/:rentalId"
            element={<RentalConfirmationPage />}
          />
          {/* ✅ RUTA DE PRUEBA PARA VERIFICAR PAGOS */}
          <Route path="/test-payment" element={<SimplePaymentTest />} />
          {/* ✅ NUEVAS RUTAS DE ESTADO DE PAGO PARA CHECKOUT API - DEBEN IR ANTES */}
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/failure" element={<PaymentFailurePage />} />
          <Route path="/payment/pending" element={<PaymentPendingPage />} />
          {/* ✅ RUTA LEGACY PARA COMPATIBILIDAD */}
          <Route path="/payment/status" element={<PaymentStatusPage />} />
          {/* ✅ NUEVA RUTA PARA PAGOS UNIFICADOS - DEBE IR DESPUÉS */}
          <Route
            path="/payment/:rentalId"
            element={
              <PrivateRoute>
                <UnifiedPaymentPage />
              </PrivateRoute>
            }
          />

          {/* Rutas protegidas */}
          <Route
            path="/perfil"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/cuenta"
            element={
              <PrivateRoute>
                <AccountPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/account/cvu-configuration"
            element={
              <PrivateRoute>
                <CVUConfiguration />
              </PrivateRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <PrivateRoute>
                <UserBookings />
              </PrivateRoute>
            }
          />
          <Route
            path="/bookings/:id"
            element={
              <PrivateRoute>
                <BookingDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/items/:itemId/book"
            element={
              <PrivateRoute>
                <CreateBooking />
              </PrivateRoute>
            }
          />
          <Route
            path="/publicar"
            element={
              <PrivateRoute>
                <CreateItemPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/mis-pagos"
            element={
              <PrivateRoute>
                <PaymentHistoryPage />
              </PrivateRoute>
            }
          />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/ayuda" element={<AyudaPage />} />
          <Route path="/terminos-y-condiciones" element={<TerminosPage />} />
          <Route path="/politica-de-privacidad" element={<PrivacidadPage />} />
          <Route
            path="/politica-de-cookies"
            element={<PoliticaCookiesPage />}
          />
          <Route
            path="/edit-item/:itemId"
            element={
              <PrivateRoute>
                <EditItemPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/reclamos"
            element={
              <PrivateRoute>
                <UserDisputesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/reclamos/nuevo"
            element={
              <PrivateRoute>
                <DisputeForm />
              </PrivateRoute>
            }
          />
          <Route
            path="/reclamos/:id"
            element={
              <PrivateRoute>
                <DisputeDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/split-payment"
            element={
              <PrivateRoute>
                <PaymentSplitPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/mensajes/:rentalId"
            element={
              <PrivateRoute>
                <MessagesPage />
              </PrivateRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer /> {/* El Footer también se renderiza una sola vez, aquí. */}
      <FloatingTourButton /> {/* El botón flotante es global. */}
    </div>
  );
}

// --- 3. App MODIFICADO PARA ENVOLVER CON TourProvider ---
function App() {
  return (
    <AuthProvider>
      <Router>
        {/* TourProvider debe estar DENTRO de Router para funcionar */}
        <TourProvider>
          <AppContent />
        </TourProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
